import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useParams, useHistory, Link } from 'react-router-dom';
import BackLink from '../components/BackLink';
import { useApi } from '../apiHelpers';
import { useToast } from '../components/Toast/ToastContext';
import useUserStore from '../stores/useUserStore';
import { getDisplayStatus } from '../utils/date-utils';
import VehicleAssignmentModal from '../TourTimeline/VehicleAssignmentModal';
import MessageThread from '../components/MessageThread/MessageThread';
import PageLayout from '../components/ui/PageLayout';
import { IconButton, buttonClass } from '../components/ui/Button';
import UiTooltip from '../components/ui/Tooltip';
import { IconClose } from '../components/ui/icons';
import { Field, TextArea } from '../components/ui/Field';
import SearchField from '../components/ui/SearchField';
import Tag from '../components/ui/Tag';
import { CATEGORY } from '../components/ui/categoryColors';
import LogoSpinner from '../components/ui/LogoSpinner';
import Dialog from '../components/ui/Dialog';
import { Popover } from '../components/ui/Popover';
import { MAP_TIP, MapTipStyle } from '../components/ui/PopoverMapTip';
import { Panel } from '../components/ui/Panel';
import { DataTable } from '../components/table/DataTable';
import dayjs from 'dayjs';
import { MapContainer, TileLayer, Marker, Tooltip, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

/* ── Map helpers ── */

const makePinSvg = (color, label) => {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 44" width="32" height="44">
    <path d="M16 0C7.2 0 0 7.2 0 16c0 12 16 28 16 28s16-16 16-28C32 7.2 24.8 0 16 0z" fill="${color}" stroke="#000" stroke-width="0.5" opacity="0.9"/>
    <text x="16" y="18" text-anchor="middle" fill="white" font-size="12" font-weight="bold" font-family="sans-serif">${label}</text>
  </svg>`;
  return L.divIcon({
    className: '',
    html: svg,
    iconSize: [32, 44],
    iconAnchor: [16, 44],
    tooltipAnchor: [0, -44],
  });
};

const FitTourBounds = ({ coords }) => {
  const map = useMap();
  useEffect(() => {
    if (coords.length > 0) {
      map.fitBounds(coords, { padding: [40, 40], maxZoom: 7 });
    }
  }, [coords, map]);
  return null;
};

/* ── Shared UI helpers ── */

const Card = ({ children, className = '' }) => (
  <Panel padding="md" className={className}>
    {children}
  </Panel>
);

const SectionHeading = ({ children }) => (
  <h3 className="text-[11px] text-on-surface-variant uppercase tracking-widest font-semibold mb-4 flex items-center gap-2">
    <span className="w-1 h-3.5 rounded-full bg-primary/60 inline-block" />
    {children}
  </h3>
);

const DataRow = ({ label, children, border }) => (
  <div className={`flex justify-between items-baseline py-1.5 ${border ? 'border-t border-gray-700/50 mt-1 pt-2.5' : ''}`}>
    <span className="text-on-surface-variant text-sm">{label}</span>
    <span className="text-on-surface text-sm text-right">{children}</span>
  </div>
);

const STATUS_HEX = {
  confirmed: CATEGORY.blue.ink.hex,
  packing: CATEGORY.purple.ink.hex,
  active: CATEGORY.green.ink.hex,
  completed: CATEGORY.emerald.ink.hex,
  cancelled: CATEGORY.red.ink.hex,
};

/* ── Timeline visualization ── */

const TourTimeline = ({ tour }) => {
  const events = (tour.events || [])
    .slice()
    .sort((a, b) => dayjs(a.event_date).diff(dayjs(b.event_date)));

  if (events.length === 0) return null;

  const points = [];

  if (tour.window_start) {
    points.push({ date: tour.window_start, label: 'Depart', type: 'transit' });
  }

  events.forEach((ev) => {
    points.push({ date: ev.event_date, label: ev.name, type: 'show' });
  });

  if (tour.window_end) {
    points.push({ date: tour.window_end, label: 'Return', type: 'transit' });
  }

  if (points.length < 2) return null;

  return (
    <div className="overflow-x-auto py-2">
      <div className="relative flex items-start" style={{ minWidth: points.length * 100 }}>
        {/* Connecting line */}
        <div
          className="absolute top-[7px] bg-gray-750 h-[2px]"
          style={{ left: 24, right: 24 }}
        />

        {points.map((pt, i) => (
          <div
            key={`${pt.date}-${i}`}
            className="flex flex-col items-center relative z-10"
            style={{ flex: 1, minWidth: 80 }}
          >
            <div
              className={`w-4 h-4 rounded-full border-2 ${
                pt.type === 'show'
                  ? 'bg-primary border-primary'
                  : 'bg-surface-container-highest border-outline'
              }`}
            />
            <span className="text-[11px] text-on-surface-variant mt-2 whitespace-nowrap">
              {dayjs(pt.date).format('MMM D')}
            </span>
            <span
              className={`text-[11px] mt-0.5 whitespace-nowrap truncate max-w-[90px] text-center ${
                pt.type === 'show' ? 'text-on-surface font-medium' : 'text-on-surface-variant'
              }`}
            >
              {pt.label}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ── Add Event Picker (dropdown) ── */

const AddEventPicker = ({ open, anchorRef, availableEvents, onAdd, onClose }) => {
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!open) setSearch('');
  }, [open]);

  const filtered = useMemo(() => {
    if (!search.trim()) return availableEvents;
    const q = search.toLowerCase();
    return availableEvents.filter(
      (e) =>
        e.name?.toLowerCase().includes(q) ||
        e.location?.toLowerCase().includes(q)
    );
  }, [availableEvents, search]);

  return (
    <Popover
      open={open}
      onClose={onClose}
      anchor={anchorRef}
      label="Add an event to this tour"
      side="bottom"
      align="end"
      footer={(
        <button
          onClick={onClose}
          className={buttonClass('quiet', 'sm')}
        >
          Cancel
        </button>
      )}
    >
      <div className="mb-2">
        <SearchField
          label="Search events"
          value={search}
          onChange={setSearch}
          placeholder="Search events..."
          autoFocus
        />
      </div>
      <div className="-mx-4 -mb-4 max-h-56 overflow-y-auto">
        {filtered.length === 0 ? (
          <p className="text-on-surface-variant text-sm text-center py-6">
            No available events
          </p>
        ) : (
          filtered.map((ev) => (
            <button
              key={ev.id}
              onClick={() => onAdd(ev.id)}
              className="w-full text-left px-4 py-2.5 hover:bg-gray-700/50 transition-colors border-b border-gray-700/30 last:border-b-0"
            >
              <span className="text-sm text-on-surface block">{ev.name}</span>
              <span className="text-[11px] text-on-surface-variant">
                {dayjs(ev.event_date).format('MMM D, YYYY')}
                {ev.location && ` \u2022 ${ev.location}`}
              </span>
            </button>
          ))
        )}
      </div>
    </Popover>
  );
};

/* ── Delete Confirmation Dialog ── */

const ConfirmDialog = ({ open, message, onConfirm, onCancel }) => {
  const cancelRef = useRef(null);
  return (
    <Dialog
      open={open}
      onClose={onCancel}
      alert
      tone="danger"
      size="md"
      label="Delete tour"
      initialFocus={cancelRef}
      hideClose
      footer={(
        <>
          <button
            ref={cancelRef}
            onClick={onCancel}
            className={buttonClass('quiet')}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className={buttonClass('danger')}
          >
            Delete
          </button>
        </>
      )}
    >
      <p className="m-0 text-on-surface text-sm">{message}</p>
    </Dialog>
  );
};

/* ── Shows table ── */

const SHOW_COLUMNS = [
  {
    id: 'event',
    header: 'Event',
    cell: ({ row }) => {
      const ev = row.original;
      return (
        <>
          <Link
            to={`/events/${ev.id}`}
            className="text-on-surface font-medium hover:text-primary transition-colors"
          >
            {ev.name}
          </Link>
          {ev.is_double_show && (
            <Tag size="sm" tone="warn" className="ml-2">2x</Tag>
          )}
        </>
      );
    },
  },
  {
    id: 'date',
    header: 'Date',
    meta: { cellClass: 'text-on-surface whitespace-nowrap' },
    cell: ({ row }) => {
      const ev = row.original;
      return (
        <>
          {dayjs(ev.event_date).format('MMM D, YYYY')}
          {ev.end_date && ev.end_date !== ev.event_date && (
            <span className="text-on-surface-variant">
              {' '}&rarr; {dayjs(ev.end_date).format('MMM D')}
            </span>
          )}
        </>
      );
    },
  },
  {
    id: 'location',
    header: 'Location',
    meta: { cellClass: 'text-on-surface-variant max-w-0 truncate' },
    cell: ({ row }) => row.original.location || <span className="text-on-surface-disabled">&mdash;</span>,
  },
  {
    id: 'drones',
    header: 'Drones',
    meta: { align: 'right', numeric: true, cellClass: 'whitespace-nowrap' },
    cell: ({ row }) => {
      const ev = row.original;
      const lsCount = ev.ls_drone_count || 0;
      const pyroCount = ev.pyro_drone_count || 0;
      return (
        <>
          <span className="text-on-surface">{lsCount + pyroCount}</span>
          {pyroCount > 0 && (
            <span className="text-on-surface-variant text-xs ml-1">
              ({pyroCount}p + {lsCount}ls)
            </span>
          )}
        </>
      );
    },
  },
  {
    id: 'status',
    header: 'Status',
    meta: { align: 'center' },
    cell: ({ row }) => (
      <Tag hex={STATUS_HEX[getDisplayStatus(row.original)]} className="capitalize">
        {getDisplayStatus(row.original)}
      </Tag>
    ),
  },
  {
    id: 'remove',
    header: () => <span className="sr-only">Remove</span>,
    meta: { align: 'center', cellClass: 'w-12' },
    cell: ({ row, table }) => (
      <UiTooltip label="Remove from tour">
        <IconButton
          label="Remove from tour"
          size="sm"
          onClick={() => table.options.meta.onRemove(row.original.id)}
        >
          <IconClose />
        </IconButton>
      </UiTooltip>
    ),
  },
];

/* ── Main TourDetail component ── */

const TourDetail = () => {
  const { id } = useParams();
  const history = useHistory();
  const api = useApi();
  const toast = useToast();
  const user = useUserStore((state) => state.user);
  // Assigning vehicles to a tour is producer/operations only (managers can
  // manage the vehicle fleet but not tour assignment) — mirrors the backend
  // PUT /tours/:id/vehicles gate.
  const canAssignVehicles = !!(
    user?.isadmin || user?.can_admin || user?.can_producer || user?.can_operations
  );

  const [tour, setTour] = useState(null);
  const [allEvents, setAllEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  // Messages

  // Inline editing
  const [editingName, setEditingName] = useState(false);
  const [nameValue, setNameValue] = useState('');
  const [editingNotes, setEditingNotes] = useState(false);
  const [notesValue, setNotesValue] = useState('');
  const nameInputRef = useRef(null);
  const notesInputRef = useRef(null);

  // Add event picker
  const [showAddEvent, setShowAddEvent] = useState(false);
  const addEventRef = useRef(null);

  // Delete confirmation
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Vehicle assignment
  const [showVehicles, setShowVehicles] = useState(false);

  const fetchTour = useCallback(async () => {
    try {
      const data = await api.get(`/tours/${id}`);
      setTour(data);
      setNameValue(data.name || '');
      setNotesValue(data.notes || '');
    } catch (err) {
      toast.error('Failed to load tour');
      history.push('/events/tours');
    }
  }, [id]);

  const fetchEvents = useCallback(async () => {
    try {
      const data = await api.get('/events');
      setAllEvents(data || []);
    } catch (err) {
      // Non-critical, just limits add-event picker
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    const init = async () => {
      await Promise.all([fetchTour(), fetchEvents()]);
      setLoading(false);
    };
    init();
  }, [id]);

  // Focus name input when editing
  useEffect(() => {
    if (editingName && nameInputRef.current) {
      nameInputRef.current.focus();
      nameInputRef.current.select();
    }
  }, [editingName]);

  useEffect(() => {
    if (editingNotes && notesInputRef.current) {
      notesInputRef.current.focus();
    }
  }, [editingNotes]);

  /* ── Actions ── */

  const saveName = async () => {
    setEditingName(false);
    if (nameValue.trim() === (tour.name || '')) return;
    try {
      await api.patch(`/tours/${id}`, { body: { name: nameValue.trim() } });
      setTour((prev) => ({ ...prev, name: nameValue.trim() }));
    } catch (err) {
      toast.error('Failed to update name');
      setNameValue(tour.name || '');
    }
  };

  const saveNotes = async () => {
    setEditingNotes(false);
    if (notesValue === (tour.notes || '')) return;
    try {
      await api.patch(`/tours/${id}`, { body: { notes: notesValue } });
      setTour((prev) => ({ ...prev, notes: notesValue }));
    } catch (err) {
      toast.error('Failed to update notes');
      setNotesValue(tour.notes || '');
    }
  };

  const handleAddEvent = async (eventId) => {
    setShowAddEvent(false);
    try {
      await api.post(`/tours/${id}/events`, { body: { event_id: eventId } });
      toast.success('Event added to tour');
      // Re-fetch tour to get updated stats
      await fetchTour();
      await fetchEvents();
    } catch (err) {
      toast.error('Failed to add event');
    }
  };

  const handleRemoveEvent = async (eventId) => {
    try {
      await api.delete(`/tours/${id}/events/${eventId}`);
      toast.success('Event removed');
      // Check if tour is now empty
      const remaining = (tour.events || []).filter((e) => e.id !== eventId);
      if (remaining.length === 0) {
        history.push('/events/tours');
        return;
      }
      await fetchTour();
      await fetchEvents();
    } catch (err) {
      toast.error('Failed to remove event');
    }
  };

  const handleDeleteTour = async () => {
    setShowDeleteConfirm(false);
    try {
      await api.delete(`/tours/${id}`);
      toast.success('Tour deleted');
      history.push('/events/tours');
    } catch (err) {
      toast.error('Failed to delete tour');
    }
  };

  /* ── Derived data ── */

  const events = useMemo(
    () =>
      (tour?.events || [])
        .slice()
        .sort((a, b) => dayjs(a.event_date).diff(dayjs(b.event_date))),
    [tour]
  );

  const availableEvents = useMemo(
    () =>
      allEvents
        .filter(
          (e) =>
            !e.tour_id &&
            e.status !== 'cancelled' &&
            e.status !== 'completed'
        )
        .sort((a, b) => dayjs(a.event_date).diff(dayjs(b.event_date))),
    [allEvents]
  );

  const transitDays = useMemo(() => {
    if (!tour?.window_start || !tour?.window_end || events.length === 0) return null;
    const firstShow = events[0].event_date;
    const lastShow = events[events.length - 1].end_date || events[events.length - 1].event_date;
    const outbound = dayjs(firstShow).diff(dayjs(tour.window_start), 'day');
    const inbound = dayjs(tour.window_end).diff(dayjs(lastShow), 'day');
    const totalDays = dayjs(tour.window_end).diff(dayjs(tour.window_start), 'day');
    return { outbound, inbound, totalDays };
  }, [tour, events]);

  if (loading) return <LogoSpinner label="Loading the tour" />;
  if (!tour) return null;

  return (
    <PageLayout
      kind="record"
      thread={<MessageThread entityType="tour" entityId={id} />}
      header={(
        <>
      <BackLink to="/events/tours" label="Tours" className="mb-5" />

      {/* ── Full-width header card ── */}
      {/* Mirrors EventDetail's header block (left on the old grey on purpose, Mannie likes that page), so the twin record pages match. */}
      <div className="bg-gray-800 border border-gray-700/60 rounded-md overflow-hidden relative mb-5">
        {/* Top accent bar */}
        <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-cyan-500/80 via-cyan-500/40 to-transparent" />

        {/* Zone A: Identity */}
        <div className="px-5 pt-5 pb-4">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
            <div className="min-w-0">
              {editingName ? (
                <Field
                  ref={nameInputRef}
                  variant="inline"
                  aria-label="Tour name"
                  type="text"
                  value={nameValue}
                  onChange={(e) => setNameValue(e.target.value)}
                  onBlur={saveName}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') saveName();
                    if (e.key === 'Escape') {
                      setNameValue(tour.name || '');
                      setEditingName(false);
                    }
                  }}
                  className="!h-auto !text-2xl sm:!text-3xl font-bold !w-full max-w-md"
                />
              ) : (
                <UiTooltip label="Click to edit">
                <h1
                  className="text-2xl sm:text-3xl font-bold text-white tracking-tight cursor-pointer hover:text-on-surface transition-colors inline-flex items-center gap-2"
                  onClick={() => setEditingName(true)}
                >
                  {tour.name}
                  <svg className="w-4 h-4 text-on-surface-disabled" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931z" /></svg>
                </h1>
                </UiTooltip>
              )}
              <div className="flex flex-wrap items-center gap-2 mt-2.5">
                <Tag hex={CATEGORY.cyan.ink.hex}>
                  {events.length} {events.length === 1 ? 'show' : 'shows'}
                </Tag>
                {tour.committed_total > 0 && (
                  <Tag>
                    {tour.committed_total} drones
                    {tour.committed_pyro > 0 && <span className="text-purple-400 ml-1">({tour.committed_pyro}P)</span>}
                  </Tag>
                )}
                {transitDays && (
                  <Tag>
                    {dayjs(tour.window_start).format('MMM D')} → {dayjs(tour.window_end).format('MMM D')} ({transitDays.totalDays}d)
                  </Tag>
                )}
                {(tour.vehicles || []).map((v) => {
                  const unusable = ['ooc', 'retired'].includes(v.operational_status);
                  return (
                    <UiTooltip
                      key={v.id}
                      label={`${v.name || v.display_code} · ${v.drone_capacity ?? 0} drones · ${v.crew_capacity ?? 0} seats${unusable ? ` · ${v.operational_status}` : ''}`}
                    >
                      <Tag tone={unusable ? 'bad' : 'neutral'} className={unusable ? 'line-through' : ''}>
                        🚐 {v.name || v.display_code}
                      </Tag>
                    </UiTooltip>
                  );
                })}
                {/* An assigned truck is what makes capacity a question, not
                    transport_mode: a show can carry a truck AND shipped freight,
                    and the mode stays "shipping" there. */}
                {(tour.vehicles || []).length > 0 && tour.committed_total > tour.vehicle_drone_capacity && (
                  <UiTooltip label={`Biggest show needs ${tour.committed_total} drones (incl. buffer) · assigned vehicles haul ${tour.vehicle_drone_capacity}`}>
                    <Tag tone="bad">
                      {tour.committed_total}/{tour.vehicle_drone_capacity} drone capacity
                    </Tag>
                  </UiTooltip>
                )}
                {tour.transport_arranged && (
                  <UiTooltip label="External transport arranged for one or more shows on this tour">
                    <Tag tone="info">transport arranged</Tag>
                  </UiTooltip>
                )}
                {canAssignVehicles && (
                  <UiTooltip label="Assign vehicles">
                    <button
                      onClick={() => setShowVehicles(true)}
                      className={buttonClass('quiet', 'sm')}
                    >
                      {(tour.vehicles || []).length > 0 ? 'Edit vehicles' : '+ Vehicles'}
                    </button>
                  </UiTooltip>
                )}
                {canAssignVehicles && (
                  <UiTooltip label="Open this tour in the Transport Scheduler">
                    <Link
                      to={`/tour-timeline?focusEvent=${tour.events?.[0]?.id || ''}&focusDate=${tour.window_start || tour.events?.[0]?.event_date || ''}`}
                      className={buttonClass('quiet', 'sm')}
                    >
                      Manage transport <span aria-hidden>→</span>
                    </Link>
                  </UiTooltip>
                )}
              </div>
              {/* Inline notes */}
              <div className="mt-2.5">
                {editingNotes ? (
                  <TextArea
                    ref={notesInputRef}
                    aria-label="Tour notes"
                    value={notesValue}
                    onChange={(e) => setNotesValue(e.target.value)}
                    onBlur={saveNotes}
                    onKeyDown={(e) => {
                      if (e.key === 'Escape') {
                        setNotesValue(tour.notes || '');
                        setEditingNotes(false);
                      }
                    }}
                    rows={2}
                    className="max-w-lg"
                  />
                ) : (
                  <UiTooltip label="Click to edit">
                    <p
                      className="text-sm text-on-surface-variant cursor-pointer hover:text-on-surface transition-colors inline-block"
                      onClick={() => setEditingNotes(true)}
                    >
                      {tour.notes || 'Add notes...'}
                    </p>
                  </UiTooltip>
                )}
              </div>
            </div>

            {/* Date range callout */}
            {events.length > 0 && (
              <div className="hidden sm:flex flex-col items-end flex-shrink-0">
                <span className="text-xs text-on-surface-variant uppercase tracking-wide">Shows</span>
                <span className="text-lg font-semibold text-white mt-0.5">
                  {dayjs(events[0].event_date).format('MMM D')}
                  {events.length > 1 && ` – ${dayjs(events[events.length - 1].event_date).format('MMM D')}`}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Timeline — embedded in header */}
        {events.length > 0 && (
          <div className="px-5 py-3 border-t border-gray-700/50">
            <TourTimeline tour={tour} />
          </div>
        )}

        {/* Zone B: Utility toolbar */}
        <div className="px-5 py-2.5 border-t border-gray-700/50 flex flex-wrap items-center gap-1">
          {tour.creator_firstname && (
            <span className="text-[11px] text-on-surface-variant mr-2">
              Created by {tour.creator_firstname} {tour.creator_lastname}
              {tour.created_at && ` on ${dayjs(tour.created_at).format('MMM D, YYYY')}`}
            </span>
          )}
          <div className="flex-1" />
          <button
            onClick={() => setShowDeleteConfirm(true)}
            className={buttonClass('danger-quiet', 'sm')}
          >
            Delete Tour
          </button>
        </div>
      </div>

        </>
      )}
    >

          {/* Tour Route Map */}
          {(() => {
            const mapEvents = events.filter(e => e.launch_latitude && e.launch_longitude);
            if (mapEvents.length === 0) return null;
            const coords = mapEvents.map(e => [e.launch_latitude, e.launch_longitude]);
            return (
              <Card className="overflow-hidden">
                <SectionHeading>Tour Route</SectionHeading>
                <div className="rounded-md overflow-hidden border border-gray-700/40" style={{ height: 280 }}>
                  <MapContainer center={coords[0]} zoom={5} style={{ height: '100%', width: '100%' }} scrollWheelZoom={false} attributionControl={false}>
                    <TileLayer url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}" />
                    <TileLayer url="https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}" />
                    <FitTourBounds coords={coords} />
                    {/* Route line connecting shows */}
                    {coords.length > 1 && (
                      <Polyline
                        positions={coords}
                        pathOptions={{ color: CATEGORY.cyan.mark.hex, weight: 2, opacity: 0.6, dashArray: '8 6' }}
                      />
                    )}
                    {/* Numbered pins for each show */}
                    {mapEvents.map((e, i) => (
                      <Marker key={e.id} position={[e.launch_latitude, e.launch_longitude]} icon={makePinSvg(CATEGORY.cyan.mark.hex, i + 1)}>
                        <Tooltip direction="top" className={MAP_TIP}>
                          <div style={{ fontWeight: 'bold', color: '#fff' }}>{e.name}</div>
                          <div style={{ fontSize: 11, color: 'var(--on-surface-variant)' }}>{dayjs(e.event_date).format('MMM D, YYYY')}</div>
                          {e.location && <div style={{ fontSize: 11, color: 'var(--on-surface-variant)' }}>{e.location}</div>}
                        </Tooltip>
                      </Marker>
                    ))}
                  </MapContainer>
                </div>
                <div className="flex gap-3 mt-2 text-[11px] text-on-surface-variant">
                  {mapEvents.map((e, i) => (
                    <span key={e.id} className="flex items-center gap-1.5">
                      <span className="w-4 h-4 rounded-full bg-cyan-600 text-[9px] text-white font-bold flex items-center justify-center">{i + 1}</span>
                      {e.name}
                    </span>
                  ))}
                </div>
              </Card>
            );
          })()}

          {/* Shows table */}
          <Card>
            <div className="flex items-center justify-between mb-4">
              <SectionHeading>Shows</SectionHeading>
              <div>
                <button
                  ref={addEventRef}
                  onClick={() => setShowAddEvent(!showAddEvent)}
                  className={buttonClass('quiet')}
                >
                  + Add Event
                </button>
                <AddEventPicker
                  open={showAddEvent}
                  anchorRef={addEventRef}
                  availableEvents={availableEvents}
                  onAdd={handleAddEvent}
                  onClose={() => setShowAddEvent(false)}
                />
              </div>
            </div>

            <DataTable
              data={events}
              columns={SHOW_COLUMNS}
              getRowId={(ev) => String(ev.id)}
              meta={{ onRemove: handleRemoveEvent }}
              on="panel"
              empty={<div className="text-center text-on-surface-variant py-8 text-sm">No events in this tour</div>}
            />
          </Card>

          {/* Tour Summary */}
          <Card>
            <SectionHeading>Tour Summary</SectionHeading>
            <div className="space-y-0.5">
              <DataRow label="Fleet needed">
                <span className="font-medium">{tour.committed_total || 0}</span>
                <span className="text-on-surface-variant text-xs ml-1">
                  ({tour.committed_pyro || 0} pyro, {tour.committed_ls || 0} standard)
                </span>
              </DataRow>
              {(tour.committed_batteries != null && tour.committed_batteries > 0) && (
                <DataRow label="Batteries">{tour.committed_batteries}</DataRow>
              )}
              {tour.window_start && tour.window_end && (
                <>
                  <DataRow label="Transit window" border>
                    {dayjs(tour.window_start).format('MMM D')} &rarr;{' '}
                    {dayjs(tour.window_end).format('MMM D')}
                    {transitDays && (
                      <span className="text-on-surface-variant text-xs ml-1">
                        ({transitDays.totalDays} days)
                      </span>
                    )}
                  </DataRow>
                  {transitDays && (
                    <DataRow label="Transit breakdown">
                      <span className="text-on-surface-variant text-xs">
                        Outbound: {transitDays.outbound} day{transitDays.outbound !== 1 ? 's' : ''}
                        {' '}&middot;{' '}
                        Inbound: {transitDays.inbound} day{transitDays.inbound !== 1 ? 's' : ''}
                      </span>
                    </DataRow>
                  )}
                </>
              )}
            </div>
          </Card>

      {/* Delete confirmation dialog */}
      <MapTipStyle />

      {showVehicles && (
        <VehicleAssignmentModal
          tourId={tour.id}
          tourName={tour.name}
          assigned={tour.vehicles || []}
          onClose={() => setShowVehicles(false)}
          onSaved={(updated) => {
            setShowVehicles(false);
            setTour(updated);
          }}
        />
      )}

      <ConfirmDialog
        open={showDeleteConfirm}
        message={`Delete "${tour.name}"? This will unlink all events from the tour but won't delete the events themselves.`}
        onConfirm={handleDeleteTour}
        onCancel={() => setShowDeleteConfirm(false)}
      />
    </PageLayout>
  );
};

export default TourDetail;
